<template>
  <div class="p-grid">
    <Card class="style p-col">
      <template #title>
        <h3 class="p-m-0">
          <strong>Import and Export</strong>
        </h3>
      </template>
      <template #content>
        <TabView v-model:activeIndex="activeTab">
          <TabPanel v-if="canImport" header="Import">

            <div class="stepper">
              <div class="steps-container">
                <template v-for="(step, index) in steps" :key="index">
                  <div class="step" :class="{ active: currentStep === index }">
                    <div class="circle">{{ index + 1 }}</div>
                    <div class="label">{{ step }}</div>
                  </div>
                  <div v-if="index < steps.length - 1" class="line"></div>
                </template>
              </div>

              <div v-if="currentStep === 0" class="step-content" style="margin-top: 1rem;">
                <Dropdown
                    v-model="selectedOption"
                    :disabled="isUploading"
                    :options="options"
                    optionLabel="name"
                    placeholder="Select import type"
                    style="margin-top: 1rem;"
                />
                <div class="actions">
                  <Button :disabled="selectedOption === null || selectedOption === ''" class="p-button-success"
                          label="Next" @click="nextStep"/>
                </div>
              </div>

              <div v-if="currentStep === 1" class="step-content" style="margin-top: 1rem;">
                <h3>Upload file</h3>
                <div
                    class="drop-zone"
                    @dragover.prevent
                    @drop.prevent="handleDrop"
                    @click="triggerFileInput"
                >
                  <span v-if="!file">Drag and drop file to here to upload</span>
                  <div style="display: flex; justify-content: space-around; align-items: center;" v-else>
                    <strong>{{ file.name }}</strong>
                    <div
                        style="display: flex; justify-content: space-around; align-items: center; gap: 1rem; flex-wrap: wrap;">
                      <span>{{ (file.size / 1024).toFixed() }} kb</span>
                      <Button class="p-button-danger p-button-rounded p-button-text" icon="mdi mdi-close"
                              @click.stop="clearFile" :loading="isUploading"/>
                    </div>
                  </div>
                  <input type="file" ref="fileInput" class="hidden" @change="handleFileChange"/>
                </div>
                <div v-if="uploadError" class="error">{{ uploadError }}</div>
                <div class="actions">
                  <Button class="p-button-success" label="Back" :loading="isUploading" @click="prevStep"/>
                  <Button class="p-button-success" label="Next" :loading="isUploading" :disabled="!file"
                          @click="handleUploadFile"/>
                </div>
              </div>

              <div v-if="currentStep === 2" class="step-content" style="padding-top: 1rem;">
                <h3>Successful {{ selectedOption.name }}!</h3>
                <p>File: <strong>{{ file?.name }}</strong></p>
                <p>Process Time: <strong>{{ uploadResult?.processTime }}</strong></p>
                <p>ACL Permission Group: <strong>{{ uploadResult?.aclPermissionGroup }}</strong></p>
                <p>Created: <strong>{{ uploadResult?.created }}</strong></p>
                <p>Deleted: <strong>{{ uploadResult?.deleted }}</strong></p>
                <p>Updated: <strong>{{ uploadResult?.updated }}</strong></p>
                <p>Skipped: <strong>{{ uploadResult?.skipped }}</strong></p>
                <p>Import Process Status Type: <strong>{{ uploadResult?.importProcessStatusType }}</strong></p>
                <div v-if="uploadResult?.warnings?.length > 0">
                  <strong>Warnings:</strong>
                  <p v-for="warning in uploadResult.warnings" v-bind:key="warning">{{ warning }}</p>
                </div>
                <div v-if="uploadResult?.errors?.length > 0">
                  <strong>Errors:</strong>
                  <p v-for="error in uploadResult.error" v-bind:key="error">{{ error }}</p>
                </div>
                <div class="actions">
                  <Button class="p-button-success" label="Upload another one" @click="reset"/>
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel v-if="canExport" header="Export">
            <div class="cards-wrapper">
              <div
                  v-for="(card, index) in cards"
                  :key="index"
                  class="card-wrapper"
              >
                <div class="card-content">
                  <i :class="`mdi mdi-${card.icon}`" style="font-size: 18px;"></i>
                  <p style="font-size: 18px!important; margin-top: 1rem; color: #82B541;">{{ card.title }}</p>
                  <p>{{ card.description }}</p>
                </div>
                <div class="overlay">
                  <div class="loader">
                    <Button icon="mdi mdi-download" rounded class="p-button-success"
                            :disabled="loading === true && loadingType === card.type"
                            :loading="loading" @click="download(card.type)"/>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>
        <p v-if="!canImport && !canExport" class="p-m-0">
          Access denied
        </p>
      </template>
    </Card>

    <Toast/>
  </div>
</template>
<script>
import {
  getAlarmPointsExport,
  getOrganizationsExport,
  getParticipantGroupsExport,
  getParticipantsExport,
} from "@/api/export";
import {importFile, importLDAP} from "@/api/adminImport";
import { formatApiError } from "@/utils/apiError";

export default {
  computed: {
    allowedTabs() {
      return [
        this.canImport ? 'import' : null,
        this.canExport ? 'export' : null,
      ].filter(Boolean);
    },
    canImport() {
      return (this.$store.state.adminPermissions || []).includes('IMPORT');
    },
    canExport() {
      return (this.$store.state.adminPermissions || []).includes('EXPORT');
    },
  },
  data: () => ({
    steps: ['Type', 'Upload', 'Result'],
    file: null,
    dropdownOptions: [
      {label: 'Option A', value: 'A'},
      {label: 'Option B', value: 'B'},
      {label: 'Option C', value: 'C'}
    ],
    selectedOption: null,
    uploadError: null,
    loading: false,
    loadingType: "alarmpoints",
    activeTab: 0,
    currentStep: 0,
    cards: [
      {
        icon: 'bell-outline',
        title: 'Alarms',
        description: 'Alarms information',
        type: "alarmpoints",
      },
      {
        icon: 'office-building-outline',
        title: 'Organizations',
        description: 'Structure and description of organizations',
        type: "organizations",
      },
      {
        icon: 'account-multiple',
        title: 'Participant Groups',
        description: 'Groups of participants and their description',
        type: "participant-groups",
      },
      {
        icon: 'account-group',
        title: 'Participants',
        description: 'List all system participants',
        type: "participants",
      },
    ],
    options: [
      {name: "Excel import", value: "excel"},
      {name: "Xml ixarma 4 import", value: "xml"},
      {name: "Xml Pisa import", value: "pisa"},
      {name: "LDAP-import", value: "ldap"},
    ],
    isUploading: false,
    uploadResult: null
  }),
  watch: {
    allowedTabs: {
      immediate: true,
      handler(tabs) {
        if (this.activeTab >= tabs.length) {
          this.activeTab = 0;
        }
      }
    }
  },
  methods: {
    nextStep() {
      this.currentStep++;
    },
    prevStep() {
      this.currentStep--;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const f = event.target.files[0];
      this.validateFile(f);
    },
    handleDrop(event) {
      const f = event.dataTransfer.files[0];
      this.validateFile(f);
    },
    validateFile(file) {
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        this.uploadError = 'The file is too big (max. 5MB)';
        this.file = null;
      } else {
        this.uploadError = '';
        this.file = file;
      }
    },
    reset() {
      this.currentStep = 0;
      this.selectedOption = '';
      this.file = null;
      this.uploadError = '';
    },
    getApiMethod(type) {
      switch (type) {
        case "alarmpoints":
          return getAlarmPointsExport();
        case "organizations":
          return getOrganizationsExport();
        case "participant-groups":
          return getParticipantGroupsExport();
        case "participants":
          return getParticipantsExport();
        default:
          return getAlarmPointsExport();
      }
    },
    click() {
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
      }, 2000)
    },
    download(type) {
      if (!this.canExport) {
        return Promise.resolve(false);
      }

      this.loading = true;
      this.loadingType = type;
      return this.getApiMethod(type).then((res) => {
            if (res.status === 200) {
              var a = document.createElement("a");
              document.body.appendChild(a);
              a.style = "display: none";
              const blob = new Blob([res.data], {type: "xlsx"});
              var url = window.URL.createObjectURL(blob);
              a.href = url;
              let headerLine = res.headers["content-disposition"];
              a.download = `${Date.now()}.xlsx`;
              if (headerLine) {
                a.download = headerLine.split("=").pop().replace('"', "");
              }
              a.click();
              window.URL.revokeObjectURL(url);
              this.loading = false;
            }
            return Promise.reject();
          },
          (error) => {
            this.$toast.add({
              severity: "error",
              summary: this._t("label_error"),
              detail: formatApiError(error, "Something went wrong", ": "),
              life: 3000,
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    clearFile() {
      this.file = null;
      this.$refs.fileInput.value = "";
    },
    handleUploadFile() {
      if (!this.canImport) {
        return;
      }

      let formData = new FormData();
      formData.append("file", this.file);

      const uploadFunction = () => {
        if (this.selectedOption.value !== "ldap") {
          return importFile(this.selectedOption.value, formData);
        } else {
          return importLDAP(formData);
        }
      };

      if (this.selectedOption) {
        this.isUploading = true;
        uploadFunction()
            .then((response) => {
              if (!response.data[0].errors.length) {
                this.uploadResult = response.data[0]
                this.$toast.add({
                  severity: "success",
                  summary: "Success",
                  detail: "File uploaded",
                  life: 3000,
                });
                this.isUploading = false;
                this.nextStep();
              } else {
                this.$toast.add({
                  severity: "error",
                  summary: "Error",
                  detail: "Something went wrong with file uploading",
                  life: 3000,
                });
                this.uploadError = response.data[0].errors[0]
                setTimeout(() => {
                  this.uploadError = ''
                }, 5000)
                this.isUploading = false;
              }
            })
            .catch(() => {
              this.$toast.add({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong with file uploading",
                life: 3000,
              });
              this.isUploading = false;
            });
      }
    },
  },
};
</script>

<style scoped>
.cards-wrapper {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  overflow-x: auto;
}

.card-wrapper {
  position: relative;
  min-width: 250px;
  padding: 1.5rem;
  border-radius: 1rem;
  background-color: #f0f4f8;
  color: #333;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}

.card-wrapper:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
}

.card-content .icon {
  font-size: 2rem;
  color: #3498db;
  margin-bottom: 0.5rem;
}

.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: inherit;
  transition: opacity 0.3s ease;
  z-index: 10;
}

.card-wrapper:hover .overlay {
  opacity: 1;
  pointer-events: auto;
}

.stepper {
  max-width: 600px;
  margin: auto;
  padding: 2rem;
  font-family: sans-serif;
}

.step.active {
  color: white;
  font-weight: bold;
}

.step-content {
  text-align: center;
}

.actions {
  margin-top: 2rem;
}

.actions button {
  margin: 0 0.5rem;
  padding: 0.5rem 1.5rem;
}

.drop-zone {
  border: 2px dashed #aaa;
  padding: 3rem 2rem;
  border-radius: 8px;
  cursor: pointer;
}

.drop-zone:hover {
  border-color: #689F38;
}

.hidden {
  display: none;
}

.error {
  color: red;
  margin-top: 1rem;
}

.steps-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafd;
  border: 1px solid #e0e6ed;
  padding: 1rem 2rem;
  border-radius: 10px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  border-radius: 25px;
}

.circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f1f3f5;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  border: 2px solid #dee2e6;
  z-index: 1;
}

.label {
  margin-top: 0.5rem;
  font-size: 14px;
  color: #495057;
}

.step.active .circle {
  background: #e6f9f3;
  color: #689F38;
  border-color: #689F38;
}

.step.active .label {
  color: #689F38;
  font-weight: 600;
}

.line {
  height: 2px;
  background: #dee2e6;
  flex: 1;
  margin: 0 0.5rem;
  z-index: 0;
}
</style>
